import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Spinner, Image } from "react-bootstrap";
import axios from "../../context/AxiosInstance";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { FaUserEdit, FaUserPlus } from "react-icons/fa";
import "../../styles/rh-modern-ui.css";

const MySwal = withReactContent(Swal);

const EmployeFormModal = ({ show, handleClose, isEditMode, selectedEmploye, token, refreshData }) => {
  const [form, setForm] = useState({
    nom: "",
    prenom: "",
    email: "",
    contact: "",
    date_naissance: "",
    date_embauche: "",
    date_sortie: "",
    type_cadre: "",
    genre: "",
  });

  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isEditMode && selectedEmploye) {
      setForm({
        nom: selectedEmploye.nom || "",
        prenom: selectedEmploye.prenom || "",
        email: selectedEmploye.email || "",
        contact: selectedEmploye.contact || "",
        date_naissance: selectedEmploye.date_naissance || "",
        date_embauche: selectedEmploye.date_embauche || "",
        date_sortie: selectedEmploye.date_sortie || "",
        type_cadre: selectedEmploye.type_cadre || "",
        genre: selectedEmploye.genre || "",
      });

      if (selectedEmploye.photo_employe) {
        setPreview(`/api/static/photos/${selectedEmploye.photo_employe}`);
      }
    } else {
      setForm({
        nom: "",
        prenom: "",
        email: "",
        contact: "",
        date_naissance: "",
        date_embauche: "",
        date_sortie: "",
        type_cadre: "",
        genre: "",
      });
      setPhoto(null);
      setPreview(null);
    }
    setErrors({});
  }, [show, isEditMode, selectedEmploye]);

  const validate = () => {
    const newErrors = {};
    if (!form.nom) newErrors.nom = "Nom requis";
    if (!form.prenom) newErrors.prenom = "Prénom requis";
    if (!form.email) newErrors.email = "Email requis";
    if (!form.contact) newErrors.contact = "Contact requis";
    if (!form.date_naissance) newErrors.date_naissance = "Date de naissance requise";
    if (!form.date_embauche) newErrors.date_embauche = "Date d’embauche requise";
    if (!form.type_cadre) newErrors.type_cadre = "Type de cadre requis";
    if (!form.genre) newErrors.genre = "Genre requis";
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    setPhoto(file);
    if (file) {
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const valErrors = validate();
    if (Object.keys(valErrors).length > 0) {
      setErrors(valErrors);
      return;
    }

    setLoading(true);
    const formData = new FormData();
    Object.keys(form).forEach((key) => formData.append(key, form[key]));
    if (photo) formData.append("photo_employe", photo);

    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "multipart/form-data",
      },
    };

    try {
      if (isEditMode) {
        await axios.patch(`/employes/${selectedEmploye.id_employe}`, formData, config);
        MySwal.fire("✅ Employé modifié", "", "success");
      } else {
        await axios.post("/employes/", formData, config);
        MySwal.fire("✅ Employé ajouté", "", "success");
      }
      refreshData();
      handleClose();
    } catch (err) {
      const backend = err.response?.data?.detail;
      if (backend?.message) {
        MySwal.fire("Erreur ❌", backend.message, "error");
      } else if (typeof backend === "string") {
        MySwal.fire("Erreur ❌", backend, "error");
      } else {
        MySwal.fire("Erreur ❌", "Une erreur s'est produite", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} backdrop="static" size="lg" className="rh-modern-modal rh-modal-lg" centered>
      <Modal.Header closeButton className="rh-modal-header">
        <div>
          <Modal.Title>
            {isEditMode ? (
              <><FaUserEdit className="me-2" />Modifier un employé</>
            ) : (
              <><FaUserPlus className="me-2" />Ajouter un employé</>
            )}
          </Modal.Title>
          <div className="rh-modal-subtitle">
            {isEditMode ? "Mettez à jour les informations de la fiche employé." : "Créez une nouvelle fiche dans le registre RH."}
          </div>
        </div>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body className="rh-modal-body">
          <div className="rh-modal-section">
            <div className="rh-modal-section-title">Identité & contact</div>
            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Nom *</Form.Label>
                  <Form.Control type="text" name="nom" value={form.nom} onChange={handleChange} isInvalid={!!errors.nom} />
                  <Form.Control.Feedback type="invalid">{errors.nom}</Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Prénom *</Form.Label>
                  <Form.Control type="text" name="prenom" value={form.prenom} onChange={handleChange} isInvalid={!!errors.prenom} />
                  <Form.Control.Feedback type="invalid">{errors.prenom}</Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Email *</Form.Label>
                  <Form.Control type="email" name="email" value={form.email} onChange={handleChange} isInvalid={!!errors.email} />
                  <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Contact *</Form.Label>
                  <Form.Control type="text" name="contact" value={form.contact} onChange={handleChange} isInvalid={!!errors.contact} />
                  <Form.Control.Feedback type="invalid">{errors.contact}</Form.Control.Feedback>
                </Form.Group>
              </div>

              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Date de naissance *</Form.Label>
                  <Form.Control type="date" name="date_naissance" value={form.date_naissance} onChange={handleChange} isInvalid={!!errors.date_naissance} />
                  <Form.Control.Feedback type="invalid">{errors.date_naissance}</Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Date d’embauche *</Form.Label>
                  <Form.Control type="date" name="date_embauche" value={form.date_embauche} onChange={handleChange} isInvalid={!!errors.date_embauche} />
                  <Form.Control.Feedback type="invalid">{errors.date_embauche}</Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Date de sortie</Form.Label>
                  <Form.Control type="date" name="date_sortie" value={form.date_sortie} onChange={handleChange} />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Type de cadre *</Form.Label>
                  <Form.Select name="type_cadre" value={form.type_cadre} onChange={handleChange} isInvalid={!!errors.type_cadre}>
                    <option value="">-- Sélectionner --</option>
                    <option value="Contractuel">Contractuel</option>
                    <option value="Fonctionnaire">Fonctionnaire</option>
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">{errors.type_cadre}</Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Genre *</Form.Label>
                  <Form.Select name="genre" value={form.genre} onChange={handleChange} isInvalid={!!errors.genre}>
                    <option value="">-- Sélectionner --</option>
                    <option value="Masculin">Masculin</option>
                    <option value="Féminin">Féminin</option>
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">{errors.genre}</Form.Control.Feedback>
                </Form.Group>
              </div>
            </div>
          </div>

          <div className="rh-modal-section">
            <div className="rh-modal-section-title">Photo de profil</div>
            <div className="row align-items-center">
              <div className="col-md-6">
                <Form.Group className="mb-0">
                  <Form.Label>Importer une photo</Form.Label>
                  <Form.Control type="file" accept="image/*" onChange={handlePhotoChange} />
                </Form.Group>
              </div>
              <div className="col-md-6">
                {preview && (
                  <div className="rh-photo-preview">
                    <Image src={preview} fluid thumbnail style={{ maxHeight: "140px" }} />
                    <div className="mt-1 text-muted small">Aperçu</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer className="rh-modal-footer">
          <Button variant="outline-secondary" onClick={handleClose}>
            Annuler
          </Button>
          <Button type="submit" variant="primary" disabled={loading}>
            {loading ? <Spinner animation="border" size="sm" /> : isEditMode ? "Enregistrer" : "Ajouter"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default EmployeFormModal;
